import { Badge } from '@teach/ui'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import { categoryText } from '@/features/moderation/lib/reviewText'
import { parseViolationRef } from '@/features/moderation/lib/violationRef'
import { ViolationThumb } from '@/features/moderation/ViolationThumb'
import type { Violation } from '@/features/moderation/types'
import { violationPlace, type ViolationGo, type ViolationPlace } from './violationPlace'
import { messagesNow, useUiLang } from '@/i18n'
import { messages } from './messages'

/** «Картинка · <место>», плюс `(+N)`, если картинка лежит ещё где-то. */
const imagePlace = (place: ViolationPlace, count: number, located: boolean): ViolationPlace => {
  if (!located) return place
  const more = count > 1 ? ` (+${count - 1})` : ''
  return { label: `${messagesNow(messages).image} · ${place.label}${more}`, go: place.go }
}

interface Props {
  violation: Violation
  lessons: { id: number }[]
  onGo: (go: ViolationGo) => void
}

/** Хуки нельзя звать в цикле — строка сама подписана на блоки своего урока (как `LessonTargetRow`). */
export const ViolationRow = ({ violation, lessons, onGo }: Props) => {
  // `violationPlace` читает язык интерфейса в момент вызова — подписываемся на его смену.
  useUiLang()
  const target = parseViolationRef(violation.ref)
  // Картинка сама по себе места не знает — его называет первое ПОНЯТНОЕ из
  // `places` (подпись и переход); форму ref, которую студия ещё не знает,
  // пропускаем, а не печатаем сырой строкой. Миниатюра — по URL картинки.
  const places = target.kind === 'image' ? (violation.places ?? []) : []
  const where =
    places.map(parseViolationRef).find((t) => t.kind !== 'unknown') ?? target
  const lessonExists = where.kind === 'block' && lessons.some((l) => l.id === where.lessonId)
  const { data: blocks } = useGetLessonBlocksQuery(where.kind === 'block' ? where.lessonId : 0, {
    skip: !lessonExists,
  })
  const place = imagePlace(violationPlace(where, lessons, blocks), places.length, where !== target)
  const { go } = place
  const body = (
    <>
      {target.kind === 'image' ? <ViolationThumb url={target.url} /> : null}
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium break-words">{place.label}</p>
        <Badge variant="destructive">{categoryText(violation.category)}</Badge>
        <p className="text-sm text-muted-foreground break-words">{violation.reason}</p>
      </div>
    </>
  )
  // Без своей рамки: строка живёт внутри карточки отказа (`ReviewStatusPanel`).
  const cls = 'flex w-full items-start gap-3 rounded-xl p-2.5 text-left'
  return go ? (
    <button
      type="button"
      className={`${cls} transition-colors hover:bg-destructive/10`}
      onClick={() => onGo(go)}
    >
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  )
}
